import { DataLoader } from 'src/app/services/data-loader/data-loader';
import {
    DataLoaderResult,
    mergeDataLoaderResultStatus,
} from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { BiVizDashboardCompanyCampaignKpisDailySqlMap } from '../sql-maps/biviz-dashboard-company-campaign-kpis-daily-sql-map';
import { BiVizDashboardCompanyPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-company-player-kpis-daily-sql-map';

export interface RoasDailyD30DataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

export class RoasDailyD30DataLoaderFactory extends Factory<
    DataLoader,
    RoasDailyD30DataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'RoasDailyD30DataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: RoasDailyD30DataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const {
                    campaignKPIsSQL,
                    // campaignKPIs,
                    campaignKPIsDsColumnsStatus,
                    campaignKPIsTableFullPath,
                } = getCampaignKPIs();

                const {
                    playerKPIsSQL,
                    // playerKPIs,
                    playerKPIsDsColumnsStatus,
                    playerKPIsTableFullPath,
                } = getplayerKPIs();

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH campaignKPIs AS
(
  SELECT  
    ${campaignKPIsSQL.splitSelect}date
    , SUM(cost_usd) AS cost_usd
    , SUM(cost_pln) AS cost_pln
  FROM \`${campaignKPIsTableFullPath}\`
  ${campaignKPIsSQL.where}
  GROUP BY ${campaignKPIsSQL.splitGroupBy}date
)

, playerKPIs AS
(
    SELECT
      ${playerKPIsSQL.splitSelect}date AS date
      , SUM(daily_cohort_kpis.revenue_lte_d30_maturity_d30_net_usd) AS revenue_lte_d30_maturity_d30_net_usd
      , SUM(daily_cohort_kpis.revenue_lte_d30_maturity_d30_net_pln) AS revenue_lte_d30_maturity_d30_net_pln
      , SUM(daily_cohort_kpis.revenue_lte_d30_maturity_d30_gross_usd) AS revenue_lte_d30_maturity_d30_gross_usd
      , SUM(daily_cohort_kpis.revenue_lte_d30_maturity_d30_gross_pln) AS revenue_lte_d30_maturity_d30_gross_pln
    FROM \`${playerKPIsTableFullPath}\`
    ${playerKPIsSQL.where}
    GROUP BY ${playerKPIsSQL.splitGroupBy}date
)

SELECT
  ${campaignKPIsSQL.splitGroupBy}date
  , SAFE_DIVIDE(revenue_lte_d30_maturity_d30_net_usd, cost_usd) AS ROAS_d30_net_usd
  , SAFE_DIVIDE(revenue_lte_d30_maturity_d30_net_pln, cost_pln) AS ROAS_d30_net_pln
  , SAFE_DIVIDE(revenue_lte_d30_maturity_d30_gross_usd, cost_usd) AS ROAS_d30_gross_usd
  , SAFE_DIVIDE(revenue_lte_d30_maturity_d30_gross_pln, cost_pln) AS ROAS_d30_gross_pln
FROM campaignKPIs
LEFT JOIN playerKPIs USING (date${
                    campaignKPIsSQL.splitGroupBy !== '' ? ', split_group' : ''
                })
ORDER BY ${campaignKPIsSQL.splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);
                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    'day'
                );

                // merge status messages
                const pendingStatusMessage = [
                    campaignKPIsDsColumnsStatus.pendingStatusMessage,
                    playerKPIsDsColumnsStatus.pendingStatusMessage,
                ].join('\n');

                this.emitter.emit({
                    data,
                    queryContext,
                    status: mergeDataLoaderResultStatus([
                        campaignKPIsDsColumnsStatus.pendingDataLoaderResultStatus,
                        playerKPIsDsColumnsStatus.pendingDataLoaderResultStatus,
                    ]),
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });

                function getCampaignKPIs() {
                    const campaignKPIs =
                        BiVizDashboardCompanyCampaignKpisDailySqlMap;

                    const campaignKPIsDatasourceColumns =
                        queryContext.datasourceColumns;
                    // filter from context TRAFFIC_SOURCE
                    // const campaignKPIsDatasourceColumns =
                    //     queryContext.datasourceColumns.filter(
                    //         (dsCol) => dsCol.id !== 'ATTRIBUTION_TRAFFIC_SOURCE'
                    //     );
                    // identify columns not supported in datasource
                    const campaignKPIsDsColumns =
                        DataLoader.separatePresentAndMissingDims(
                            campaignKPIsDatasourceColumns,
                            campaignKPIs.sqlMap.columns
                        );

                    const campaignKPIsDsColumnsStatus =
                        DataLoader.getMissingColumnsStatus(
                            campaignKPIsDsColumns.missing
                        );

                    const campaignKPIsSQL = campaignKPIs.compileSQLStatements(
                        campaignKPIsDsColumns.present,
                        queryContext.settings,
                        options,
                        {
                            where: [
                                `date >= DATE_SUB(CURRENT_DATE(), INTERVAL 59 DAY)`,
                                `date <= DATE_SUB(CURRENT_DATE(), INTERVAL 31 DAY)`,
                            ],
                        }
                    );

                    const campaignKPIsTableFullPath = generateTableFullPath(
                        options.gcpProjectId,
                        campaignKPIs.sqlMap.dataset,
                        campaignKPIs.sqlMap.tableId
                    );
                    return {
                        campaignKPIsSQL,
                        // campaignKPIs,
                        campaignKPIsDsColumnsStatus,
                        campaignKPIsTableFullPath,
                    };
                }

                function getplayerKPIs() {
                    const playerKPIs =
                        BiVizDashboardCompanyPlayerKpisDailySqlMap;

                    const playerKPIsDatasourceColumns =
                        queryContext.datasourceColumns;

                    // filter from context TRAFFIC_SOURCE
                    // const playerKPIsDatasourceColumns =
                    //     queryContext.datasourceColumns.filter(
                    //         (dsCol) => dsCol.id !== 'ATTRIBUTION_TRAFFIC_SOURCE'
                    //     );

                    // identify columns not supported in datasource
                    const playerKPIsDsColumns =
                        DataLoader.separatePresentAndMissingDims(
                            playerKPIsDatasourceColumns,
                            playerKPIs.sqlMap.columns
                        );

                    const playerKPIsDsColumnsStatus =
                        DataLoader.getMissingColumnsStatus(
                            playerKPIsDsColumns.missing
                        );

                    const playerKPIsSQL = playerKPIs.compileSQLStatements(
                        playerKPIsDsColumns.present,
                        queryContext.settings,
                        options,
                        {
                            where: [
                                // `date >= DATE_SUB(CURRENT_DATE(), INTERVAL 37 DAY)`,
                                // `date <= DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY)`,
                            ],
                        }
                    );

                    const playerKPIsTableFullPath = generateTableFullPath(
                        options.gcpProjectId,
                        playerKPIs.sqlMap.dataset,
                        playerKPIs.sqlMap.tableId
                    );
                    return {
                        playerKPIsSQL,
                        // playerKPIs,
                        playerKPIsDsColumnsStatus,
                        playerKPIsTableFullPath,
                    };
                }
            };

            await standardRun(this, queryContext, process);
        });
    }
}
